import React, { useState } from 'react';

import { Mail, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { AddTeamIcon } from '#/assets/svgs';
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    Input,
    Button,
} from '#/components/ui';
import { apiEndpoints, queryKeys, usePostMutation } from '#/services';
import { EMAIL_REGEX } from '#/utils';

const AddTeamMember = () => {
    const [openInviteModal, setOpenInviteModal] = useState(false);
    const [emails, setEmails] = useState([{ id: Date.now(), value: '', error: '' }]);

    const { mutate: addTeamMemberApiCall, isLoading: addTeamMemberApiLoading } = usePostMutation({
        url: apiEndpoints.ADD_TEAM_MEMBER,
        queryKey: [queryKeys.TEAMS],
        onSuccess: handleAddSuccess,
    });

    function handleAddSuccess(resp) {
        toast.success(resp?.detail || 'Invitation sent successfully!');
        setOpenInviteModal(false);
        handleClose();
    }

    const handleAddEmail = () => {
        setEmails(prev => [...prev, { id: Date.now(), value: '', error: '' }]);
    };

    const handleRemoveEmail = id => {
        setEmails(prev => prev.filter(email => email.id !== id));
    };

    const handleEmailChange = (id, value) => {
        setEmails(prev =>
            prev.map(email =>
                email.id === id
                    ? {
                          ...email,
                          value,
                          error: value && !EMAIL_REGEX.test(value) ? 'Invalid email' : '',
                      }
                    : email
            )
        );
    };

    const handleSendInvite = () => {
        const updatedEmails = emails.map(email => ({
            ...email,
            error: !email.value ? 'Email is required' : !EMAIL_REGEX.test(email.value) ? 'Invalid email' : '',
        }));
        setEmails(updatedEmails);
        const hasErrors = updatedEmails.some(e => e.error);
        if (hasErrors) return;
        const payload = updatedEmails.map(e => e.value);
        addTeamMemberApiCall({
            payload: {
                emails: payload,
            },
        });
    };
    const handleClose = () => {
        setEmails([{ id: Date.now(), value: '', error: '' }]);
    };
    const handleModalChange = isOpen => {
        setOpenInviteModal(isOpen);
        if (!isOpen) handleClose();
    };

    return (
        <Dialog open={openInviteModal} onOpenChange={handleModalChange}>
            <DialogTrigger asChild>
                <Button>+ Add New Team Member</Button>
            </DialogTrigger>

            <DialogContent className='max-w-[400px] max-h-[700px] bg-white overflow-auto p-6 gap-0'>
                <DialogHeader className='items-start space-y-2'>
                    <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full '>
                        <AddTeamIcon />
                    </div>
                    <DialogTitle className='pt-3 text-lg font-medium text-gray900'>Invite Team Members</DialogTitle>
                    <DialogDescription asChild>
                        <div className='text-start text-gray500 text-sm font-normal'>
                            Invite others to access and use the platform with their own accounts.
                        </div>
                    </DialogDescription>
                </DialogHeader>

                <div className='text-start text-gray700 pt-5 text-sm font-normal mb-0'>Email address</div>
                <div className='space-y-4'>
                    {emails?.map(email => (
                        <div key={email.id} className='flex items-start gap-2'>
                            <div className='w-full'>
                                <div className='relative'>
                                    <Input
                                        type='email'
                                        placeholder='you@example.com'
                                        value={email?.value}
                                        onChange={e => handleEmailChange(email.id, e.target.value)}
                                        className='mt-1 h-11 block w-full pl-8 border placeholder-gray500 border-gray300'
                                    />
                                    <Mail className='w-5 h-5 absolute top-[12px] left-2 text-gray500' />
                                </div>
                                {email.error && <p className='text-xs text-red-500 mt-1'>{email.error}</p>}
                            </div>
                            {emails.length > 1 && (
                                <button
                                    type='button'
                                    onClick={() => handleRemoveEmail(email.id)}
                                    className='mt-2 text-gray-400 hover:text-red-500'
                                >
                                    <Trash2 className='w-4 h-4' />
                                </button>
                            )}
                        </div>
                    ))}

                    <button
                        type='button'
                        onClick={handleAddEmail}
                        className='text-textPrimary text-sm font-medium hover:underline cursor-pointer'
                    >
                        + Add another
                    </button>

                    <div className='flex justify-between gap-2 w-full  items-center pt-4'>
                        <Button
                            variant='outline'
                            onClick={() => setOpenInviteModal(false)}
                            className=' w-[170px] text-gray700 h-10 cursor-pointer bg-transparent border-gray300'
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendInvite}
                            isLoading={addTeamMemberApiLoading}
                            className='  text-white w-[170px]  h-10 cursor-pointer'
                        >
                            Send Invite
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export { AddTeamMember };
